import { useState, useEffect, useCallback } from "react";

const PROXY = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const token = getToken();
  const res = await fetch(PROXY + path, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const STATUS_COLORS = {
  draft:     { color: "#6b7280", bg: "#f3f4f6" },
  sent:      { color: "#2563eb", bg: "#eff6ff" },
  confirmed: { color: "#d97706", bg: "#fffbeb" },
  shipped:   { color: "#7c3aed", bg: "#f5f3ff" },
  received:  { color: "#15803d", bg: "#f0fdf4" },
  cancelled: { color: "#dc2626", bg: "#fef2f2" },
};

const STATUS_FLOW = ["draft", "sent", "confirmed", "shipped", "received"];

const S = {
  card:    { background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", marginBottom: 14 },
  cardHdr: { padding: "11px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 600, fontSize: 13 },
  th:      { padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#6b7280", background: "#f9fafb", whiteSpace: "nowrap" },
  td:      { padding: "9px 12px", borderBottom: "1px solid #f3f4f6", verticalAlign: "middle", fontSize: 13 },
  btn:     (c = "#F47920") => ({ padding: "6px 14px", borderRadius: 7, fontSize: 12, cursor: "pointer", background: c, color: "#fff", border: "none", fontFamily: "inherit", fontWeight: 600 }),
  btnOut:  { padding: "6px 14px", borderRadius: 7, fontSize: 12, cursor: "pointer", background: "#fff", color: "#374151", border: "1px solid #d1d5db", fontFamily: "inherit" },
  input:   { width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", outline: "none", marginBottom: 10 },
  label:   { fontSize: 11, color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 3 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal:   { background: "#fff", borderRadius: 12, padding: 24, width: 680, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background: bg, color, padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{label}</span>
);

export default function PagePurchaseOrders({ toast, products = [] }) {
  const [tab,       setTab]       = useState("pos");
  const [suggestions, setSuggestions] = useState([]);
  const [payModal,  setPayModal]  = useState(null); // {po}
  const [payAmount, setPayAmount] = useState("");
  const [pos,       setPos]       = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading,   setLoading]   = useState(true);

  // modals
  const [showPoModal,  setShowPoModal]  = useState(false);
  const [editPo,       setEditPo]       = useState(null);   // null = new PO, object = edit mode
  const [showSupModal, setShowSupModal] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);

  // new PO form
  const [poForm, setPoForm] = useState({
    supplierId: "", supplierName: "", supplierEmail: "", supplierPhone: "",
    expectedDelivery: "", notes: "",
    items: [{ productName: "", shopifySku: "", quantity: 1, unit: "pcs", unitPrice: 0, gstRate: 18 }],
  });

  // new supplier form
  const [supForm, setSupForm] = useState({ name: "", company: "", email: "", phone: "", whatsapp: "", address: "", gstin: "" });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [poData, supData] = await Promise.all([api("/po"), api("/suppliers")]);
      loadSuggestions();
      setPos(poData.orders || []);
      setSuppliers(supData.suppliers || []);
    } catch (e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── supplier select fills form ──
  const onSelectSupplier = (id) => {
    const sup = suppliers.find((s) => s._id === id);
    if (sup) setPoForm((f) => ({ ...f, supplierId: id, supplierName: sup.name, supplierEmail: sup.email, supplierPhone: sup.phone || sup.whatsapp }));
    else setPoForm((f) => ({ ...f, supplierId: "", supplierName: "", supplierEmail: "", supplierPhone: "" }));
  };

  // ── PO item helpers ──
  const addItem = () => setPoForm((f) => ({ ...f, items: [...f.items, { productName: "", shopifySku: "", quantity: 1, unit: "pcs", unitPrice: 0, gstRate: 18 }] }));
  const removeItem = (i) => setPoForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, key, val) => setPoForm((f) => {
    const items = f.items.map((item, idx) => idx === i ? { ...item, [key]: val } : item);
    return { ...f, items };
  });

  const calcTotals = () => {
    let sub = 0, gst = 0;
    poForm.items.forEach((item) => {
      const base = (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
      sub += base;
      gst += Math.round(base * (item.gstRate === 0 || item.gstRate ? parseFloat(item.gstRate) : 18) / 100);
    });
    return { subtotal: sub, gstAmount: gst, total: sub + gst };
  };

  // ── create PO ──
  const savePo = async () => {
    if (!poForm.supplierName) { toast("Enter supplier name", "error"); return; }
    if (poForm.items.some((i) => !i.productName)) { toast("Fill all product names", "error"); return; }
    try {
      if (editPo) {
        await api("/po/" + editPo._id, { method: "PUT", body: JSON.stringify(poForm) });
        toast("Purchase Order updated ✓", "success");
      } else {
        await api("/po", { method: "POST", body: JSON.stringify(poForm) });
        toast("Purchase Order created ✓", "success");
      }
      setShowPoModal(false);
      setEditPo(null);
      resetPoForm();
      loadData();
    } catch (e) { toast(e.message, "error"); }
  };

  const openEditPo = (po) => {
    setEditPo(po);
    setPoForm({
      supplierId:       po.supplierId || "",
      supplierName:     po.supplierName || "",
      supplierEmail:    po.supplierEmail || "",
      supplierPhone:    po.supplierPhone || "",
      expectedDelivery: po.expectedDelivery ? new Date(po.expectedDelivery).toISOString().split("T")[0] : "",
      notes:            po.notes || "",
      items:            (po.items || []).map((item) => ({
        productName: item.productName || "",
        shopifySku:  item.shopifySku  || "",
        quantity:    item.quantity    || 1,
        unit:        item.unit        || "pcs",
        unitPrice:   item.unitPrice   || 0,
        gstRate:     item.gstRate === 0 || item.gstRate ? item.gstRate : 18,
      })),
    });
    setShowPoModal(true);
  };

  const deletePo = async (id) => {
    if (!window.confirm("Permanently delete this Purchase Order? This cannot be undone.")) return;
    try {
      await api("/po/" + id, { method: "DELETE" });
      toast("Purchase Order deleted ✓", "success");
      loadData();
    } catch (e) { toast(e.message, "error"); }
  };

  const resetPoForm = () => setPoForm({
    supplierId: "", supplierName: "", supplierEmail: "", supplierPhone: "",
    expectedDelivery: "", notes: "",
    items: [{ productName: "", shopifySku: "", quantity: 1, unit: "pcs", unitPrice: 0, gstRate: 18 }],
  });

  // ── update PO status ──
  const updateStatus = async (id, status) => {
    try {
      await api("/po/" + id + "/status", { method: "PUT", body: JSON.stringify({ status }) });
      toast("Status updated ✓", "success");
      loadData();
    } catch (e) { toast(e.message, "error"); }
  };

  // ── receive stock — adds qty to inventory ──
  const receiveStock = async (po) => {
    if (!window.confirm(`Receive all items from ${po.poNumber}? This will add the quantities to your inventory.`)) return;
    try {
      const data = await api("/po/" + po._id + "/receive", { method: "POST", body: JSON.stringify({}) });
      const added = (data.stockUpdates || []).filter(u => u.newStock !== null).length;
      const missing = (data.stockUpdates || []).filter(u => u.newStock === null).length;
      toast(`✅ Stock received — ${added} products updated${missing ? ", " + missing + " SKU not found" : ""}`, "success");
      loadData();
    } catch (e) { toast(e.message, "error"); }
  };

  // ── record payment ──
  const recordPayment = async () => {
    if (!payModal || !payAmount) return;
    try {
      await api("/po/" + payModal._id + "/payment", { method: "POST", body: JSON.stringify({ amount: parseFloat(payAmount) }) });
      toast("Payment recorded ✓", "success");
      setPayModal(null); setPayAmount("");
      loadData();
    } catch (e) { toast(e.message, "error"); }
  };

  // ── load reorder suggestions ──
  const loadSuggestions = async () => {
    try {
      const data = await api("/po/reorder-suggestions?threshold=10");
      setSuggestions(data.suggestions || []);
    } catch (e) { toast(e.message, "error"); }
  };

  // ── create PO from selected suggestions ──
  const createPoFromSuggestions = (selected) => {
    if (!selected.length) { toast("Select at least one product", "error"); return; }
    setEditPo(null);
    resetPoForm();
    setPoForm((f) => ({
      ...f,
      items: selected.map(s => ({
        productName: s.productName,
        shopifySku:  s.shopifySku,
        quantity:    s.suggestedQty,
        unit:        "pcs",
        unitPrice:   s.lastCost || 0,
        gstRate:     18,
      })),
    }));
    setTab("pos");
    setShowPoModal(true);
  };

  // ── send email ──
  const sendEmail = async (id) => {
    try {
      await api("/po/" + id + "/send-email", { method: "POST" });
      toast("PO sent via email ✓", "success");
      loadData();
    } catch (e) { toast(e.message, "error"); }
  };

  // ── send whatsapp ──
  const sendWhatsApp = async (id) => {
    try {
      const data = await api("/po/" + id + "/html", { method: "GET" });
      window.open(data.waUrl, "_blank");
      loadData();
    } catch (e) { toast(e.message, "error"); }
  };

  // ── download PO as PDF ──
  const downloadPdf = (po) => {
    const st = STATUS_COLORS[po.status] || STATUS_COLORS.draft;
    const rows = po.items.map((item) => {
      const base  = item.quantity * item.unitPrice;
      const gst   = Math.round(base * (item.gstRate || 0) / 100);
      return `<tr>
        <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6">${item.productName}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;color:#6b7280">${item.shopifySku || "—"}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:center">${item.quantity} ${item.unit}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:right">₹${item.unitPrice.toLocaleString("en-IN")}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:center">${item.gstRate}%</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:right;font-weight:600">₹${item.totalPrice.toLocaleString("en-IN")}</td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <title>PO ${po.poNumber}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; color: #111; background: #fff; padding: 40px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; border-bottom: 3px solid #F47920; padding-bottom: 20px; }
      .brand { font-size: 24px; font-weight: 800; color: #F47920; }
      .brand-sub { font-size: 12px; color: #9ca3af; margin-top: 2px; }
      .po-num { font-size: 20px; font-weight: 700; color: #111; }
      .po-date { font-size: 12px; color: #6b7280; margin-top: 4px; }
      .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; background: ${st.bg}; color: ${st.color}; margin-top: 6px; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 28px; }
      .info-box { background: #f9fafb; border-radius: 8px; padding: 14px 16px; }
      .info-label { font-size: 10px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
      .info-row { font-size: 13px; color: #374151; margin-bottom: 4px; }
      .info-row strong { color: #111; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
      thead tr { background: #1A3A5C; }
      thead th { padding: 10px; text-align: left; font-size: 11px; color: #fff; font-weight: 600; }
      thead th:nth-child(3), thead th:nth-child(4), thead th:nth-child(5), thead th:nth-child(6) { text-align: center; }
      thead th:nth-child(4), thead th:nth-child(6) { text-align: right; }
      tbody tr:hover { background: #fafafa; }
      .totals { margin-left: auto; width: 280px; background: #f9fafb; border-radius: 8px; padding: 16px; }
      .total-row { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 8px; color: #6b7280; }
      .total-row span:last-child { color: #111; font-weight: 500; }
      .grand-total { display: flex; justify-content: space-between; font-size: 17px; font-weight: 800; border-top: 2px solid #e5e7eb; padding-top: 10px; margin-top: 4px; color: #F47920; }
      .notes { background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 12px 16px; margin-top: 20px; font-size: 13px; color: #92400e; }
      .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 16px; }
      @media print { body { padding: 20px; } }
    </style></head><body>
    <div class="header">
      <div>
        <div class="brand">3June</div>
        <div class="brand-sub">Purchase Order</div>
      </div>
      <div style="text-align:right">
        <div class="po-num">${po.poNumber}</div>
        <div class="po-date">Date: ${new Date(po.createdAt).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</div>
        <div class="status-badge">${po.status.toUpperCase()}</div>
      </div>
    </div>

    <div class="info-grid">
      <div class="info-box">
        <div class="info-label">Supplier Details</div>
        <div class="info-row"><strong>${po.supplierName}</strong></div>
        ${po.supplierEmail ? `<div class="info-row">📧 ${po.supplierEmail}</div>` : ""}
        ${po.supplierPhone ? `<div class="info-row">📞 ${po.supplierPhone}</div>` : ""}
      </div>
      <div class="info-box">
        <div class="info-label">Order Details</div>
        <div class="info-row"><strong>PO Number:</strong> ${po.poNumber}</div>
        <div class="info-row"><strong>Created:</strong> ${new Date(po.createdAt).toLocaleDateString("en-IN")}</div>
        ${po.expectedDelivery ? `<div class="info-row"><strong>Expected Delivery:</strong> ${new Date(po.expectedDelivery).toLocaleDateString("en-IN")}</div>` : ""}
        <div class="info-row"><strong>Items:</strong> ${po.items.length}</div>
      </div>
    </div>

    <table>
      <thead><tr>
        <th>Product Name</th><th>SKU</th><th style="text-align:center">Qty</th>
        <th style="text-align:right">Unit Price</th><th style="text-align:center">GST</th>
        <th style="text-align:right">Total</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>

    <div class="totals">
      <div class="total-row"><span>Subtotal</span><span>₹${po.subtotal.toLocaleString("en-IN")}</span></div>
      <div class="total-row"><span>GST Amount</span><span>₹${po.gstAmount.toLocaleString("en-IN")}</span></div>
      <div class="grand-total"><span>Grand Total</span><span>₹${po.totalAmount.toLocaleString("en-IN")}</span></div>
    </div>

    ${po.notes ? `<div class="notes"><strong>Notes:</strong> ${po.notes}</div>` : ""}
    <div class="footer">Generated by 3June · ${new Date().toLocaleString("en-IN")} · Vasudeva Enterprises</div>
    </body></html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 600);
  };

  // ── save supplier ──
  const saveSupplier = async () => {
    if (!supForm.name) { toast("Enter supplier name", "error"); return; }
    try {
      if (editSupplier) {
        await api("/suppliers/" + editSupplier._id, { method: "PUT", body: JSON.stringify(supForm) });
        toast("Supplier updated ✓", "success");
      } else {
        await api("/suppliers", { method: "POST", body: JSON.stringify(supForm) });
        toast("Supplier added ✓", "success");
      }
      setShowSupModal(false);
      setEditSupplier(null);
      setSupForm({ name: "", company: "", email: "", phone: "", whatsapp: "", address: "", gstin: "" });
      loadData();
    } catch (e) { toast(e.message, "error"); }
  };

  const deleteSupplier = async (id) => {
    try {
      await api("/suppliers/" + id, { method: "DELETE" });
      toast("Supplier removed ✓", "success");
      loadData();
    } catch (e) { toast(e.message, "error"); }
  };

  const totals = calcTotals();

  // ── stats ──
  const totalPending  = pos.filter((p) => ["draft","sent","confirmed","shipped"].includes(p.status)).length;
  const totalReceived = pos.filter((p) => p.status === "received").length;
  const totalValue    = pos.filter((p) => p.status !== "cancelled").reduce((a, p) => a + p.totalAmount, 0);

  return (
    <div>
      {/* stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 14 }}>
        {[
          { label: "Total POs",       value: pos.length,      color: "#2563eb" },
          { label: "Pending",         value: totalPending,    color: "#d97706" },
          { label: "Received",        value: totalReceived,   color: "#15803d" },
          { label: "Total PO Value",  value: INR(totalValue), color: "#F47920" },
        ].map((k) => (
          <div key={k.label} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: "14px 16px" }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", marginBottom: 16 }}>
        {[{ id: "pos", label: "Purchase Orders (" + pos.length + ")" }, { id: "suppliers", label: "Suppliers (" + suppliers.length + ")" }, { id: "reorder", label: "🔄 Reorder" + (suggestions.length ? " (" + suggestions.length + ")" : "") }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "10px 20px", background: "none", border: "none", borderBottom: tab === t.id ? "2px solid #F47920" : "2px solid transparent", color: tab === t.id ? "#F47920" : "#6b7280", fontWeight: tab === t.id ? 600 : 400, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
            {t.label}
          </button>
        ))}
        <div style={{ flex: 1 }}/>
        {tab === "pos" && <button style={{ ...S.btn(), margin: "6px 0" }} onClick={() => { setEditPo(null); resetPoForm(); setShowPoModal(true); }}>+ New PO</button>}
        {tab === "suppliers" && <button style={{ ...S.btn(), margin: "6px 0" }} onClick={() => { setEditSupplier(null); setSupForm({ name:"",company:"",email:"",phone:"",whatsapp:"",address:"",gstin:"" }); setShowSupModal(true); }}>+ Add Supplier</button>}
      </div>

      {/* PO list */}
      {tab === "pos" && (
        <div style={S.card}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading…</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  {["PO Number","Supplier","Items","Total","Status","Payment","Expected","Actions"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {pos.map((po) => {
                    const st = STATUS_COLORS[po.status] || STATUS_COLORS.draft;
                    const nextStatus = STATUS_FLOW[STATUS_FLOW.indexOf(po.status) + 1];
                    return (
                      <tr key={po._id} onMouseEnter={(e) => e.currentTarget.style.background = "#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                        <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11, color: "#F47920" }}>{po.poNumber}</td>
                        <td style={{ ...S.td, fontWeight: 500 }}>{po.supplierName}</td>
                        <td style={S.td}>{po.items.length} item{po.items.length !== 1 ? "s" : ""}</td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{INR(po.totalAmount)}</td>
                        <td style={S.td}><Badge label={po.status} color={st.color} bg={st.bg}/></td>
                        <td style={{ ...S.td, fontSize: 11, color: "#6b7280" }}>
                          {po.expectedDelivery ? new Date(po.expectedDelivery).toLocaleDateString("en-IN") : "—"}
                        </td>
                        <td style={S.td}>
                          {po.paymentStatus === "paid"
                            ? <Badge label="Paid" color="#15803d" bg="#f0fdf4"/>
                            : po.paymentStatus === "partial"
                            ? <div><Badge label="Partial" color="#d97706" bg="#fffbeb"/><div style={{ fontSize: 9, color: "#9ca3af", marginTop: 2 }}>{INR(po.paidAmount)}/{INR(po.totalAmount)}</div></div>
                            : <Badge label="Unpaid" color="#dc2626" bg="#fef2f2"/>}
                        </td>
                        <td style={S.td}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            <button style={{ ...S.btn("#1A3A5C"), fontSize: 11, padding: "3px 8px" }} onClick={() => downloadPdf(po)}>⬇ PDF</button>
                            {po.status !== "received" && po.status !== "cancelled" && (
                              <button style={{ ...S.btnOut, fontSize: 11, padding: "3px 8px" }} onClick={() => openEditPo(po)}>✏ Edit</button>
                            )}
                            <button style={{ ...S.btnOut, fontSize: 11, padding: "3px 8px", color: "#dc2626", borderColor: "#fecaca" }} onClick={() => deletePo(po._id)}>🗑 Del</button>
                            {po.supplierEmail && po.status !== "cancelled" && (
                              <button style={{ ...S.btnOut, fontSize: 11, padding: "3px 8px" }} onClick={() => sendEmail(po._id)}>✉ Email</button>
                            )}
                            {po.supplierPhone && po.status !== "cancelled" && (
                              <button style={{ ...S.btn("#25D366"), fontSize: 11, padding: "3px 8px" }} onClick={() => sendWhatsApp(po._id)}>WA</button>
                            )}
                            {nextStatus && nextStatus !== "received" && po.status !== "cancelled" && (
                              <button style={{ ...S.btn("#2563eb"), fontSize: 11, padding: "3px 8px" }} onClick={() => updateStatus(po._id, nextStatus)}>
                                → {nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}
                              </button>
                            )}
                            {(po.status === "shipped" || po.status === "confirmed" || po.status === "sent") && (
                              <button style={{ ...S.btn("#15803d"), fontSize: 11, padding: "3px 8px" }} onClick={() => receiveStock(po)}>📥 Receive</button>
                            )}
                            {po.status !== "cancelled" && po.paymentStatus !== "paid" && (
                              <button style={{ ...S.btn("#7e22ce"), fontSize: 11, padding: "3px 8px" }} onClick={() => { setPayModal(po); setPayAmount(String((po.totalAmount||0)-(po.paidAmount||0))); }}>💰 Pay</button>
                            )}
                            {po.status !== "cancelled" && po.status !== "received" && (
                              <button style={{ ...S.btnOut, fontSize: 11, padding: "3px 8px", color: "#dc2626", borderColor: "#fecaca" }} onClick={() => updateStatus(po._id, "cancelled")}>Cancel</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {pos.length === 0 && (
                    <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: "#9ca3af", padding: 40 }}>
                      No purchase orders yet — click "+ New PO" to create one
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Suppliers list */}
      {tab === "suppliers" && (
        <div style={S.card}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                {["Name","Company","Email","Phone","WhatsApp","GSTIN","Actions"].map((h) => <th key={h} style={S.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {suppliers.map((sup) => (
                  <tr key={sup._id} onMouseEnter={(e) => e.currentTarget.style.background = "#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                    <td style={{ ...S.td, fontWeight: 500 }}>{sup.name}</td>
                    <td style={S.td}>{sup.company || "—"}</td>
                    <td style={{ ...S.td, fontSize: 11 }}>{sup.email || "—"}</td>
                    <td style={{ ...S.td, fontSize: 11 }}>{sup.phone || "—"}</td>
                    <td style={{ ...S.td, fontSize: 11 }}>{sup.whatsapp || "—"}</td>
                    <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11 }}>{sup.gstin || "—"}</td>
                    <td style={S.td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button style={{ ...S.btnOut, fontSize: 11, padding: "3px 8px" }} onClick={() => { setEditSupplier(sup); setSupForm({ name:sup.name,company:sup.company,email:sup.email,phone:sup.phone,whatsapp:sup.whatsapp,address:sup.address,gstin:sup.gstin }); setShowSupModal(true); }}>Edit</button>
                        <button style={{ ...S.btnOut, fontSize: 11, padding: "3px 8px", color: "#dc2626", borderColor: "#fecaca" }} onClick={() => deleteSupplier(sup._id)}>Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {suppliers.length === 0 && (
                  <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "#9ca3af", padding: 40 }}>
                    No suppliers yet — click "+ Add Supplier"
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* REORDER TAB */}
      {tab === "reorder" && (
        <ReorderTab
          suggestions={suggestions}
          onRefresh={loadSuggestions}
          onCreatePo={createPoFromSuggestions}
          INR={INR}
          S={S}
        />
      )}

      {/* PAYMENT MODAL */}
      {payModal && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setPayModal(null)}>
          <div style={{ ...S.modal, maxWidth: 380 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Record Payment</div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>{payModal.poNumber} · {payModal.supplierName}</div>
            <div style={{ background: "#f9fafb", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: "#6b7280" }}>Total amount</span>
                <span style={{ fontWeight: 600 }}>{INR(payModal.totalAmount)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: "#6b7280" }}>Already paid</span>
                <span style={{ fontWeight: 600, color: "#15803d" }}>{INR(payModal.paidAmount || 0)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #e5e7eb", paddingTop: 4, marginTop: 4 }}>
                <span style={{ color: "#6b7280" }}>Balance due</span>
                <span style={{ fontWeight: 700, color: "#dc2626" }}>{INR((payModal.totalAmount || 0) - (payModal.paidAmount || 0))}</span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 5, textTransform: "uppercase", letterSpacing: "1px" }}>Payment amount ₹</div>
            <input style={S.input} type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} autoFocus/>
            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
              <button style={S.btnOut} onClick={() => setPayModal(null)}>Cancel</button>
              <button style={S.btn("#7e22ce")} onClick={recordPayment}>Record ₹{payAmount || 0}</button>
            </div>
          </div>
        </div>
      )}

      {/* NEW PO MODAL */}
      {showPoModal && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowPoModal(false)}>
          <div style={S.modal}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{editPo ? "Edit Purchase Order — " + editPo.poNumber : "New Purchase Order"}</span>
              <button onClick={() => { setShowPoModal(false); setEditPo(null); resetPoForm(); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}>✕</button>
            </div>

            {/* supplier select */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={S.label}>Select Existing Supplier</label>
                <select style={{ ...S.input, marginBottom: 10 }} value={poForm.supplierId} onChange={(e) => onSelectSupplier(e.target.value)}>
                  <option value="">— Select supplier —</option>
                  {suppliers.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Or type supplier name</label>
                <input style={S.input} placeholder="Supplier name" value={poForm.supplierName} onChange={(e) => setPoForm((f) => ({ ...f, supplierName: e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Supplier Email</label>
                <input style={S.input} type="email" placeholder="supplier@email.com" value={poForm.supplierEmail} onChange={(e) => setPoForm((f) => ({ ...f, supplierEmail: e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Supplier Phone / WhatsApp</label>
                <input style={S.input} placeholder="98XXXXXXXX" value={poForm.supplierPhone} onChange={(e) => setPoForm((f) => ({ ...f, supplierPhone: e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Expected Delivery Date</label>
                <input style={S.input} type="date" value={poForm.expectedDelivery} onChange={(e) => setPoForm((f) => ({ ...f, expectedDelivery: e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Notes</label>
                <input style={S.input} placeholder="Any special instructions" value={poForm.notes} onChange={(e) => setPoForm((f) => ({ ...f, notes: e.target.value }))}/>
              </div>
            </div>

            {/* items */}
            <div style={{ fontWeight: 600, fontSize: 13, margin: "14px 0 10px" }}>Order Items</div>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
              <thead><tr>
                {["Product Name","SKU","Qty","Unit","Unit Price (₹)","GST %",""].map((h) => (
                  <th key={h} style={{ ...S.th, padding: "6px 8px" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {poForm.items.map((item, i) => (
                  <tr key={i}>
                    <td style={{ padding: "4px 4px" }}>
                      <input style={{ ...S.input, marginBottom: 0, width: "100%" }} placeholder="Product name"
                        value={item.productName} onChange={(e) => updateItem(i, "productName", e.target.value)}
                        list="product-list"/>
                      <datalist id="product-list">
                        {products.map((p) => <option key={p.id} value={p.name}/>)}
                      </datalist>
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input style={{ ...S.input, marginBottom: 0, width: 80 }} placeholder="SKU"
                        value={item.shopifySku} onChange={(e) => updateItem(i, "shopifySku", e.target.value)}/>
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input type="number" min="1" style={{ ...S.input, marginBottom: 0, width: 60, textAlign: "center" }}
                        value={item.quantity} onChange={(e) => updateItem(i, "quantity", parseFloat(e.target.value) || 1)}/>
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <select style={{ ...S.input, marginBottom: 0, width: 70 }} value={item.unit} onChange={(e) => updateItem(i, "unit", e.target.value)}>
                        {["pcs","kg","g","m","dozen","box"].map((u) => <option key={u}>{u}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input type="number" min="0" style={{ ...S.input, marginBottom: 0, width: 90, textAlign: "right" }}
                        value={item.unitPrice} onChange={(e) => updateItem(i, "unitPrice", parseFloat(e.target.value) || 0)}/>
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <select style={{ ...S.input, marginBottom: 0, width: 70 }} value={item.gstRate} onChange={(e) => updateItem(i, "gstRate", parseFloat(e.target.value))}>
                        {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <button onClick={() => removeItem(i)} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 16 }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button style={{ ...S.btnOut, fontSize: 12, marginBottom: 16 }} onClick={addItem}>+ Add Item</button>

            {/* totals */}
            <div style={{ background: "#f9fafb", borderRadius: 8, padding: 14, marginBottom: 16 }}>
              {[["Subtotal", INR(totals.subtotal)], ["GST", INR(totals.gstAmount)]].map(([l, v]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6, color: "#6b7280" }}>
                  <span>{l}</span><span style={{ color: "#111" }}>{v}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 15, borderTop: "1px solid #e5e7eb", paddingTop: 10 }}>
                <span>Grand Total</span><span style={{ color: "#F47920" }}>{INR(totals.total)}</span>
              </div>
            </div>

            <button style={{ ...S.btn(), width: "100%", padding: 11, fontSize: 14 }} onClick={savePo}>
              {editPo ? "Save Changes" : "Create Purchase Order"}
            </button>
          </div>
        </div>
      )}

      {/* SUPPLIER MODAL */}
      {showSupModal && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowSupModal(false)}>
          <div style={{ ...S.modal, width: 480 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{editSupplier ? "Edit Supplier" : "Add Supplier"}</span>
              <button onClick={() => setShowSupModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}>✕</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { label: "Name *",    key: "name",     placeholder: "Supplier name"    },
                { label: "Company",   key: "company",  placeholder: "Company name"     },
                { label: "Email",     key: "email",    placeholder: "supplier@email.com" },
                { label: "Phone",     key: "phone",    placeholder: "98XXXXXXXX"       },
                { label: "WhatsApp",  key: "whatsapp", placeholder: "WhatsApp number"  },
                { label: "GSTIN",     key: "gstin",    placeholder: "22AAAAA0000A1Z5"  },
              ].map(({ label, key, placeholder }) => (
                <div key={key}>
                  <label style={S.label}>{label}</label>
                  <input style={S.input} placeholder={placeholder} value={supForm[key]} onChange={(e) => setSupForm((f) => ({ ...f, [key]: e.target.value }))}/>
                </div>
              ))}
            </div>
            <label style={S.label}>Address</label>
            <textarea style={{ ...S.input, height: 70, resize: "vertical" }} placeholder="Full address" value={supForm.address} onChange={(e) => setSupForm((f) => ({ ...f, address: e.target.value }))}/>
            <button style={{ ...S.btn(), width: "100%", padding: 11, fontSize: 14 }} onClick={saveSupplier}>
              {editSupplier ? "Update Supplier" : "Add Supplier"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── REORDER TAB COMPONENT ──────────────────────────────────────
function ReorderTab({ suggestions, onRefresh, onCreatePo, INR, S }) {
  const [selected, setSelected] = useState({});
  const toggle = (sku) => setSelected((s) => ({ ...s, [sku]: !s[sku] }));
  const selectedItems = suggestions.filter((s) => selected[s.shopifySku || s.productName]);

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid #e5e7eb" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>🔄 Reorder Suggestions</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Products at or below 10 units — select and create a PO</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={S.btnOut} onClick={onRefresh}>↻ Refresh</button>
          <button style={{ ...S.btn(), opacity: selectedItems.length ? 1 : 0.5 }} disabled={!selectedItems.length}
            onClick={() => onCreatePo(selectedItems)}>+ Create PO ({selectedItems.length})</button>
        </div>
      </div>
      {suggestions.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>All stock levels healthy</div>
          <div style={{ fontSize: 13 }}>No products are below the reorder threshold right now.</div>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={{ ...S.th, width: 36 }}>
                <input type="checkbox"
                  checked={selectedItems.length === suggestions.length && suggestions.length > 0}
                  onChange={(e) => {
                    const all = {};
                    if (e.target.checked) suggestions.forEach((s) => { all[s.shopifySku || s.productName] = true; });
                    setSelected(all);
                  }}/>
              </th>
              {["Product","SKU","Current Stock","Suggested Qty","Last Cost","Est. Value"].map((h) => <th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {suggestions.map((s) => {
                const key = s.shopifySku || s.productName;
                const estValue = s.suggestedQty * (s.lastCost || 0);
                return (
                  <tr key={key} style={{ background: selected[key] ? "#fff7ed" : "" }}>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <input type="checkbox" checked={!!selected[key]} onChange={() => toggle(key)}/>
                    </td>
                    <td style={{ ...S.td, fontWeight: 500 }}>{s.productName}</td>
                    <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11, color: "#F47920" }}>{s.shopifySku || "—"}</td>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <span style={{ color: s.currentStock <= 5 ? "#dc2626" : "#d97706", fontWeight: 600 }}>{s.currentStock}</span>
                    </td>
                    <td style={{ ...S.td, textAlign: "center", fontWeight: 600, color: "#15803d" }}>{s.suggestedQty}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{s.lastCost ? INR(s.lastCost) : "—"}</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{estValue ? INR(estValue) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}